import { UserManagerSettings } from 'oidc-client-ts'
import { AuthProviderProps, User, UserManager } from 'oidc-react'
import sessionStore from 'store/storages/sessionStorage'

import { storage } from 'Shared/utils/storage'
import { getCurrentUrl, getDomainPort } from 'Shared/utils/url/helpers'

import {
	inactivityTimeoutSec,
	STORAGE_LAST_REQUEST_TIMESTAMP,
	STORAGE_REDIRECT_URI,
	STORAGE_USER_TOKEN
} from 'Core/constants'
import { isLocalHost } from 'Core/env'

export default class Auth {
	private static instance: Auth
	userManager: UserManager
	inactivityTimeoutSec: number

	private constructor(public settings: UserManagerSettings) {
		this.userManager = new UserManager(settings)
		this.inactivityTimeoutSec = inactivityTimeoutSec
	}

	static getInstance(settings: UserManagerSettings): Auth {
		if (!Auth.instance) {
			Auth.instance = new Auth(settings)
		}

		return Auth.instance
	}

	getConfig = (): AuthProviderProps => ({
		onBeforeSignIn: this.onBeforeSignIn,
		onSignIn: this.onSignIn,
		authority: this.settings.authority,
		clientId: this.settings.client_id,
		redirectUri: this.settings.redirect_uri,
		automaticSilentRenew: this.settings.automaticSilentRenew,
		responseType: this.settings.response_type,
		scope: this.settings.scope,
		postLogoutRedirectUri: getDomainPort()
	})

	checkLastActivity = () => {
		const lastActivityTimestamp = storage.get(STORAGE_LAST_REQUEST_TIMESTAMP) as number
		const currentTimestamp = Math.floor(Date.now() / 1000)
		const hasExpiredTimer =
			lastActivityTimestamp <= currentTimestamp - this.inactivityTimeoutSec + 1

		if (hasExpiredTimer && !isLocalHost) {
			this.signOutRedirect()
			return
		}

		const nextIntervalSec = lastActivityTimestamp - currentTimestamp + this.inactivityTimeoutSec

		setTimeout(this.checkLastActivity, nextIntervalSec * 1000)
	}

	runInactivityWorker = () => {
		setTimeout(this.checkLastActivity, this.inactivityTimeoutSec * 1000)
	}

	onBeforeSignIn() {
		const locationHref = getCurrentUrl()

		sessionStore.write(STORAGE_REDIRECT_URI, locationHref)
	}

	onSignIn(userData: User | null) {
		const appDomain = getDomainPort()

		if (userData?.access_token) {
			storage.set(STORAGE_USER_TOKEN, userData.access_token)
		}

		// removing unnecessary data (added by OIDC) from url
		const redirectUri = sessionStore.read(STORAGE_REDIRECT_URI) as string
		sessionStore.remove(STORAGE_REDIRECT_URI)

		window.location.replace(redirectUri || appDomain)
	}

	signOutRedirect = () => {
		this.userManager.signoutRedirect({
			post_logout_redirect_uri: getCurrentUrl()
		})
	}

	signInRedirect = (params?: { userId?: number }) => {
		const acrValues = params?.userId ? `login user:${params.userId}` : 'login'

		this.userManager.signinRedirect({
			acr_values: acrValues
		})
	}
}
